/**
 * translateError — the single, shared error → user-facing-copy translator.
 *
 * ──────────────────────────────────────────────────────────────────────────
 * NO-FIREBASE-IMPORT RULE (load-bearing)
 * ──────────────────────────────────────────────────────────────────────────
 * This module lives in `@order-up/common`, which depends ONLY on
 * `@order-up/models`. It MUST NOT import `firebase` / `firebase/*`,
 * `@order-up/core`, or `@order-up/client`. Doing so would (a) create an
 * illegal upward dependency and (b) pull Firebase into every consumer that
 * only wants error copy.
 *
 * Firebase / App Check errors are therefore detected STRUCTURALLY — by reading
 * the string `code` field off the error object — never via
 * `instanceof FirebaseError`. The shape we rely on:
 *   typeof (err as any).code === 'string' &&
 *     (code.startsWith('auth/') || code.startsWith('appCheck/') ||
 *      code.startsWith('app-check/'))
 * (See `resolveCandidateCode` below.) The root ESLint config additionally
 * forbids `firebase`/`firebase/*` imports under
 * `packages/common/src/errors/**` as defense-in-depth.
 * ──────────────────────────────────────────────────────────────────────────
 */

import { AppError } from './AppError';
import { ERROR_CATALOG, type CatalogEntry } from './errorCatalog';
import { FALLBACK_COPY } from './fallbackCopy';
import type {
  ErrorLocaleCatalog,
  FallbackCopyKey,
  LocalizedErrorCopy,
  LocalizedErrorKey,
} from './locales/types';

export type ErrorSeverity =
  | 'user_input'
  | 'service'
  | 'auth'
  | 'onboarding'
  | 'unknown';

export interface TranslatedError {
  /** Normalized code we resolved to; never shown raw to the user. */
  code: string;
  title: string;
  message: string;
  severity: ErrorSeverity;
  retryable: boolean;
  /** Safe debug line for support tickets (e.g. "code=auth/... status=401"). */
  technicalDetail: string;
  nextSteps?: string[];
  suppress?: boolean;
}

export interface TranslateErrorContext {
  /** e.g. "Google" — interpolated into popup-related messages. */
  providerName?: string;
  /** Designed runtime-override seam (optional, NOT fetched here). */
  overrides?: Record<string, Partial<CatalogEntry>>;
  /**
   * The reader's language: one POS language's error copy, from
   * `errorLocaleCatalog(locale)`. A code it translates renders in it (an
   * explicit `overrides` entry still wins); every other code stays English.
   * Absent → English, exactly as before.
   */
  localized?: ErrorLocaleCatalog;
}

/** Codes for "user dismissed the popup" — we render nothing for these. */
const SUPPRESS_CODES = new Set<string>([
  'auth/popup-closed-by-user',
  'auth/cancelled-popup-request',
  'POPUP_CANCELLED',
]);

/** Network-layer codes (axios/undici style) → catalog class-name keys. */
const TIMEOUT_NETWORK_CODES = new Set<string>(['ECONNABORTED', 'ETIMEDOUT']);
const CONNECTION_NETWORK_CODES = new Set<string>(['ERR_NETWORK', 'ENOTFOUND']);

const GENERIC_FALLBACK: Omit<TranslatedError, 'code' | 'technicalDetail'> = {
  title: FALLBACK_COPY['fallback:generic'].title,
  message: FALLBACK_COPY['fallback:generic'].message,
  severity: 'unknown',
  retryable: true,
};

/** A language's copy for a key, when that language translates it. */
function translatedCopy(
  localized: ErrorLocaleCatalog | undefined,
  key: string,
): LocalizedErrorCopy | undefined {
  if (!localized) return undefined;
  return Object.prototype.hasOwnProperty.call(localized.entries, key)
    ? localized.entries[key as LocalizedErrorKey]
    : undefined;
}

/** A catalog entry with its words in the reader's language, when translated. */
function applyLocale(
  base: CatalogEntry,
  translated: LocalizedErrorCopy | undefined,
): CatalogEntry {
  if (!translated) return base;
  return {
    ...base,
    title: translated.title,
    message: translated.message,
    nextSteps: translated.nextSteps ? [...translated.nextSteps] : base.nextSteps,
  };
}

/** A status fallback's title / message / nextSteps, in the reader's language. */
function fallback(
  key: FallbackCopyKey,
  localized: ErrorLocaleCatalog | undefined,
): Pick<TranslatedError, 'title' | 'message' | 'nextSteps'> {
  const copy = translatedCopy(localized, key) ?? FALLBACK_COPY[key];
  return {
    title: copy.title,
    message: copy.message,
    ...(copy.nextSteps ? { nextSteps: [...copy.nextSteps] } : {}),
  };
}

function asRecord(error: unknown): Record<string, unknown> | undefined {
  return error && typeof error === 'object'
    ? (error as Record<string, unknown>)
    : undefined;
}

function readStringCode(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

/**
 * Resolve a candidate normalized code from an arbitrary thrown value.
 *
 * Order: raw string → `.code` → API-envelope `.error.code` → AppError class
 * name. Returns undefined when nothing identifiable is present.
 */
function resolveCandidateCode(error: unknown): string | undefined {
  if (typeof error === 'string') return error;

  const rec = asRecord(error);
  if (rec) {
    const direct = readStringCode(rec.code);
    if (direct) return direct;

    // API-envelope shape: { error: { code, message } }
    const envelope = asRecord(rec.error);
    const envelopeCode = envelope ? readStringCode(envelope.code) : undefined;
    if (envelopeCode) return envelopeCode;
  }

  // AppError instance → use the constructor name as the catalog key.
  if (error instanceof AppError) {
    return error.constructor.name;
  }

  return undefined;
}

function readStatus(error: unknown): number | undefined {
  const rec = asRecord(error);
  if (!rec) return undefined;
  const status = rec.status ?? rec.statusCode;
  return typeof status === 'number' ? status : undefined;
}

/**
 * The correlation/request id for support. The `apiClient` interceptor stamps
 * `.correlationId` on the thrown error; also tolerate the raw envelope shape
 * (`error.error.correlationId` / `correlation_id`). Surfaced in
 * `technicalDetail` so a user can quote a reference instead of sending logs.
 */
function readCorrelationId(error: unknown): string | undefined {
  const rec = asRecord(error);
  if (!rec) return undefined;
  const envelope = asRecord(rec.error);
  const id =
    rec.correlationId ??
    envelope?.correlationId ??
    envelope?.correlation_id;
  return typeof id === 'string' && id.length > 0 ? id : undefined;
}

/**
 * Server-resolved decline guidance riding on the error, if present.
 *
 * Tolerates the same shapes as the other readers: stamped directly on the
 * thrown error (`.decline`) or nested in the API envelope
 * (`error.error.decline`). Validated structurally rather than trusted —
 * `title`/`message` must both be non-empty strings, or this is worth less
 * than the catalog entry it would displace and we fall through to it.
 *
 * `nextSteps` is normalized to a string array here so callers never have to
 * defend against a malformed one.
 */
function readDeclineGuidance(error: unknown):
  | { title: string; message: string; nextSteps: string[]; retryable: boolean }
  | undefined {
  const rec = asRecord(error);
  if (!rec) return undefined;
  const envelope = asRecord(rec.error);
  // Four shapes, because two different server paths produce it:
  //   * `error.decline`                  — stamped directly on a thrown error
  //   * `error.error.decline`            — pay.ts puts it beside code/message
  //   * `error.error.details.decline`    — AppError.details, which the API's
  //                                        errorHandler serializes verbatim
  //                                        (PaymentDeclinedError uses this)
  //   * `error.details.decline`          — a locally-raised AppError
  const raw =
    asRecord(rec.decline) ??
    asRecord(envelope?.decline) ??
    asRecord(asRecord(envelope?.details)?.decline) ??
    asRecord(asRecord(rec.details)?.decline);
  if (!raw) return undefined;

  const title = typeof raw.title === 'string' ? raw.title : '';
  const message = typeof raw.message === 'string' ? raw.message : '';
  if (title.length === 0 || message.length === 0) return undefined;

  return {
    title,
    message,
    nextSteps: Array.isArray(raw.nextSteps)
      ? raw.nextSteps.filter(
          (s): s is string => typeof s === 'string' && s.length > 0,
        )
      : [],
    // Absent → true, matching the generic fallback: never strand a payer who
    // might succeed on another card.
    retryable: typeof raw.retryable === 'boolean' ? raw.retryable : true,
  };
}

/** Severity inferred from an AppError subclass when the catalog has no entry. */
function severityFromAppError(name: string): ErrorSeverity {
  switch (name) {
    case 'AuthenticationError':
    case 'SessionInvalidError':
    case 'PermissionDeniedError':
    case 'StaleAuthError':
    case 'DeviceRevokedError':
    case 'AnonymousNotPermittedError':
      return 'auth';
    case 'ValidationError':
    case 'NotFoundError':
    case 'ConflictError':
      return 'user_input';
    case 'DeviceIdentityConflictError':
      return 'auth';
    case 'RateLimitError':
      return 'service';
    default:
      return 'unknown';
  }
}

function buildTechnicalDetail(
  code: string | undefined,
  status: number | undefined,
  correlationId?: string | undefined,
): string {
  const parts: string[] = [];
  parts.push(`code=${code ?? 'unknown'}`);
  if (status !== undefined) parts.push(`status=${status}`);
  if (correlationId) parts.push(`ref=${correlationId}`);
  return parts.join(' ');
}

/** Merge an optional runtime override over a base catalog entry. */
function applyOverride(
  base: CatalogEntry,
  override: Partial<CatalogEntry> | undefined,
): CatalogEntry {
  if (!override) return base;
  return { ...base, ...override };
}

/**
 * When a provider name is supplied and the matched copy is about a popup or a
 * mismatched account, prefix it with the provider (e.g. "Google: ..."). Kept
 * intentionally minimal — only popup-blocked / user-mismatch get the prefix.
 */
function maybePrefixProvider(
  code: string,
  message: string,
  providerName: string | undefined,
  translated = false,
): string {
  if (!providerName) return message;
  const eligible = code === 'auth/popup-blocked' || code === 'auth/user-mismatch';
  if (!eligible) return message;
  // A translation carries no English "popup" to test for; of the two eligible
  // codes only popup-blocked's copy is about the popup.
  if (translated ? code !== 'auth/popup-blocked' : !/popup/i.test(message)) return message;
  return `${providerName}: ${message}`;
}

/**
 * The server's own sentence, with the machine code the API client prefixes onto
 * it stripped back off.
 *
 * `apiClient` encodes the code into the message (`"CODE: real message"`) so call
 * sites can branch on `message.includes(...)`. Showing that prefix to a user
 * leaks a machine token into their copy for no benefit — the code still reaches
 * support through `technicalDetail`.
 */
function readServerMessage(error: unknown, code: string): string | undefined {
  const raw = asRecord(error)?.message;
  if (typeof raw !== 'string') return undefined;
  const prefix = `${code}: `;
  const stripped = (raw.startsWith(prefix) ? raw.slice(prefix.length) : raw).trim();
  return stripped.length > 0 ? stripped : undefined;
}

function fromEntry(
  code: string,
  entry: CatalogEntry,
  technicalDetail: string,
  providerName: string | undefined,
  translated = false,
): TranslatedError {
  return {
    code,
    title: entry.title,
    message: maybePrefixProvider(code, entry.message, providerName, translated),
    severity: entry.severity,
    retryable: entry.retryable,
    technicalDetail,
    nextSteps: entry.nextSteps,
    suppress: entry.suppress,
  };
}

/**
 * Translate any thrown value into honest, user-facing copy.
 *
 * Normalization cascade (returns the FIRST match):
 *   (a) Resolve a candidate code (string → .code → envelope .error.code →
 *       AppError class name).
 *   (b) SUPPRESS: popup-cancelled codes → suppress:true, render nothing.
 *   (c) DIRECT CATALOG HIT on the candidate code (with ctx.overrides merged
 *       over the catalog entry first).
 *   (d) Network fallbacks: ECONNABORTED/ETIMEDOUT → TimeoutError;
 *       ERR_NETWORK/ENOTFOUND → NetworkError.
 *   (d2) ZodError (structural detection) → first issue's message with the
 *       offending field path, severity user_input.
 *   (e) AppError instance with a message and no catalog hit → use its message,
 *       severity inferred from the class.
 *   (f) FALLBACK: severity-aware by HTTP status, else a generic message.
 *
 * The fallback message NEVER contains a raw `auth/...`/`appCheck/...` code and
 * NEVER echoes a `Firebase:`-prefixed error.message. `technicalDetail` always
 * carries the machine code + status for support tickets.
 */
export function translateError(
  error: unknown,
  ctx: TranslateErrorContext = {},
): TranslatedError {
  const { providerName, overrides, localized } = ctx;

  // (a) candidate code
  const code = resolveCandidateCode(error);
  const status = readStatus(error);
  const technicalDetail = buildTechnicalDetail(
    code,
    status,
    readCorrelationId(error),
  );

  // (b) suppress popup-cancelled
  if (code && SUPPRESS_CODES.has(code)) {
    return {
      code,
      title: '',
      message: '',
      severity: 'user_input',
      retryable: true,
      technicalDetail,
      suppress: true,
    };
  }

  // (b2) SERVER-RESOLVED DECLINE GUIDANCE.
  //
  // A declined card is the one case where the server genuinely knows more than
  // this catalog does: it holds the issuer's decline code and resolves it
  // through the shared decline catalog. The generic fallback deliberately
  // discards `error.message` to avoid echoing raw machine strings, and that is
  // right — but it also discarded this, which is not a raw machine string. It
  // is already-translated, audience-correct copy.
  //
  // Precedence: an explicit `ctx.overrides` entry for this code still wins
  // (the caller stated intent for this exact surface); otherwise the server's
  // specific reason beats the catalog's generic one. Without this the payer
  // sees "Your card was declined... we are not told the reason" for a decline
  // we were, in fact, told the reason for.
  const guidance = readDeclineGuidance(error);
  if (guidance && !(code && overrides?.[code])) {
    return {
      code: code ?? 'CARD_DECLINED',
      title: guidance.title,
      message: guidance.message,
      // A decline is the cardholder's to act on, not a service fault.
      severity: 'user_input',
      // The catalog's notion of retryable is "should we offer a retry", which
      // is what `retryable` means here too.
      retryable: guidance.retryable,
      technicalDetail,
      ...(guidance.nextSteps.length > 0
        ? { nextSteps: [...guidance.nextSteps] }
        : {}),
    };
  }

  // (c) direct catalog hit (override merged over base)
  if (code) {
    const base = ERROR_CATALOG[code];
    const override = overrides?.[code];
    if (base) {
      const translated = translatedCopy(localized, code);
      // Base → the reader's language → the caller's override.
      const entry = applyOverride(applyLocale(base, translated), override);
      // A validation refusal carries its specifics in the SERVER's sentence; the
      // catalog can only ever be generic for it. `message` stays the honest
      // fallback for when the server sent nothing usable. The server writes
      // English, so a reader whose language translates this code keeps the
      // translation instead.
      const resolved: CatalogEntry =
        entry.preferServerMessage && !translated
          ? { ...entry, message: readServerMessage(error, code) ?? entry.message }
          : entry;
      return fromEntry(code, resolved, technicalDetail, providerName, Boolean(translated));
    }
    // Override may introduce a code the base catalog doesn't carry. Only honor
    // it when it is a complete-enough entry (has at least a message).
    if (override && typeof override.message === 'string') {
      const synthesized: CatalogEntry = {
        title: override.title ?? fallback('fallback:generic', localized).title,
        message: override.message,
        severity: override.severity ?? 'unknown',
        retryable: override.retryable ?? true,
        nextSteps: override.nextSteps,
        suppress: override.suppress,
      };
      return fromEntry(code, synthesized, technicalDetail, providerName);
    }
  }

  // (d) network-layer codes (these live on `.code`, distinct from catalog codes)
  const rawCode = readStringCode(asRecord(error)?.code);
  if (rawCode && TIMEOUT_NETWORK_CODES.has(rawCode)) {
    return fromEntry(
      'TimeoutError',
      applyLocale(ERROR_CATALOG.TimeoutError, translatedCopy(localized, 'TimeoutError')),
      technicalDetail,
      providerName,
    );
  }
  if (rawCode && CONNECTION_NETWORK_CODES.has(rawCode)) {
    return fromEntry(
      'NetworkError',
      applyLocale(ERROR_CATALOG.NetworkError, translatedCopy(localized, 'NetworkError')),
      technicalDetail,
      providerName,
    );
  }

  // (d2) Zod validation errors — detected structurally (no zod import, per the
  // NO-FIREBASE-IMPORT dependency posture): surface the first issue's message
  // (optionally suffixed with the offending field path) so users learn what to
  // fix instead of a generic failure.
  {
    const rec = asRecord(error);
    const issues = rec?.name === 'ZodError' ? rec.issues : undefined;
    if (Array.isArray(issues) && issues.length > 0) {
      const first = asRecord(issues[0]);
      const issueMessage = readStringCode(first?.message);
      if (issueMessage) {
        const path = Array.isArray(first?.path)
          ? first.path.filter((p): p is string => typeof p === 'string').join('.')
          : '';
        return {
          code: 'ZodError',
          // The message is the schema's own sentence; only the title is ours.
          title: fallback('fallback:checkInput', localized).title,
          message: path ? `${issueMessage} (${path})` : issueMessage,
          severity: 'user_input',
          retryable: true,
          technicalDetail,
        };
      }
    }
  }

  // (e) AppError instance, message present, no catalog hit → use its message,
  // but never leak a `Firebase:`-prefixed string into user copy.
  if (error instanceof AppError) {
    const name = error.constructor.name;
    const msg = error.message;
    const generic = fallback('fallback:generic', localized);
    const safeMsg = msg && !/Firebase:/i.test(msg) ? msg : generic.message;
    return {
      code: code ?? name,
      title: generic.title,
      message: safeMsg,
      severity: severityFromAppError(name),
      retryable: true,
      technicalDetail,
    };
  }

  // (f) severity-aware fallback by HTTP status, else generic.
  if (status !== undefined) {
    if (status === 401 || status === 403) {
      return {
        code: code ?? 'unknown',
        ...fallback('fallback:signIn', localized),
        severity: 'auth',
        retryable: true,
        technicalDetail,
      };
    }
    // 402 Payment Required — a MONEY refusal, and the only status where the
    // generic "please try again" is not merely unhelpful but harmful: it is a
    // declined or refused card, and re-submitting the same one just adds
    // declines against the issuer's velocity rules. It must never fall through
    // to GENERIC_FALLBACK's `retryable: true`, which is exactly what it did
    // (this block handled 401/403, 400/409/422, 429 and >=500 — never 402).
    // Catalogued codes still win; this is the floor for an uncatalogued one.
    if (status === 402) {
      return {
        code: code ?? 'unknown',
        ...fallback('fallback:paymentDeclined', localized),
        severity: 'user_input',
        retryable: false,
        technicalDetail,
      };
    }
    if (status === 400 || status === 409 || status === 422) {
      return {
        code: code ?? 'unknown',
        ...fallback('fallback:checkInput', localized),
        severity: 'user_input',
        retryable: true,
        technicalDetail,
      };
    }
    if (status === 429) {
      return {
        code: code ?? 'unknown',
        ...fallback('fallback:slowDown', localized),
        severity: 'service',
        retryable: false,
        technicalDetail,
      };
    }
    // 404 Not Found — the last hole in this ladder. Every other 4xx an API
    // route can emit was intercepted above, so an uncatalogued 404 was the one
    // HTTP class that reached GENERIC_FALLBACK and told the user "Something
    // went wrong. Please try again." about a thing that does not exist —
    // advice that can only fail, forever. `retryable: false` for the same
    // reason 402 is: retrying cannot change the answer.
    if (status === 404) {
      return {
        code: code ?? 'unknown',
        ...fallback('fallback:notFound', localized),
        severity: 'user_input',
        retryable: false,
        technicalDetail,
      };
    }
    if (status >= 500) {
      return {
        code: code ?? 'unknown',
        ...fallback('fallback:serverError', localized),
        severity: 'service',
        retryable: true,
        technicalDetail,
      };
    }
  }

  // Generic fallback. Deliberately ignores `error.message` so we never echo a
  // raw machine code or a `Firebase:`-prefixed string to the user.
  return {
    code: code ?? 'unknown',
    ...GENERIC_FALLBACK,
    ...fallback('fallback:generic', localized),
    technicalDetail,
  };
}
